import { Image } from "../../assets/images";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useEtlynCms } from "@etlyn/etlyn-cms";

/**
 * ServiceSection — CMS-driven services with animated transitions.
 */
export const ServiceSection: React.FC<any> = ({ device }) => {
  const servicesState = useEtlynCms("services-core").state;
  const [activeIdx, setActiveIdx] = useState(0);
  const [displayIdx, setDisplayIdx] = useState(0);
  const [animState, setAnimState] = useState<"idle" | "out" | "in">("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isLoading = servicesState?.loading;
  const services: any[] = Array.isArray(servicesState?.services)
    ? servicesState.services
    : [];
  const hasServices = services.length > 0;
  const [initialDone, setInitialDone] = useState(false);

  useEffect(() => {
    if (hasServices || (!isLoading && !hasServices)) setInitialDone(true);
  }, [hasServices, isLoading]);

  /* ── animated tab switch ──────────────────────────────── */
  const switchTo = useCallback(
    (idx: number) => {
      if (idx === activeIdx || animState !== "idle") return;
      setActiveIdx(idx);
      setAnimState("out");

      // After fade-out, swap content and fade-in
      timeoutRef.current = setTimeout(() => {
        setDisplayIdx(idx);
        setAnimState("in");

        timeoutRef.current = setTimeout(() => {
          setAnimState("idle");
        }, 300);
      }, 200);
    },
    [activeIdx, animState]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const isMob = device === "mob";
  const active = services[displayIdx];

  /* ── content animation classes ────────────────────────── */
  const contentClass =
    animState === "out"
      ? "opacity-0 translate-y-2 scale-[0.99]"
      : animState === "in"
        ? "opacity-100 translate-y-0 scale-100"
        : "opacity-100 translate-y-0 scale-100";

  /* ── skeleton ─────────────────────────────────────────── */
  if (!initialDone || (isLoading && !hasServices)) {
    return (
      <section id="services" className="mb-8">
        <div className="text-center mb-8">
          <div className="mx-auto h-4 w-20 rounded bg-[#EEF1FB] mb-3 animate-pulse" />
          <div className="mx-auto h-6 w-48 rounded bg-[#EEF1FB] animate-pulse" />
        </div>
        <div className="flex justify-center gap-2 mb-8">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-9 w-28 rounded-full bg-[#EEF1FB] animate-pulse" />
          ))}
        </div>
        <div className="rounded-2xl bg-white ring-1 ring-[#DFDFE0]/60 p-8 h-64 animate-pulse" />
      </section>
    );
  }

  /* ── empty ────────────────────────────────────────────── */
  if (!hasServices) {
    return (
      <section id="services" className="mb-8 text-center py-16">
        <p className="text-sm text-[#808692]">No services available at the moment.</p>
      </section>
    );
  }

  return (
    <section id="services" className="mb-8">
      {/* Header */}
      <div className="text-center mb-8">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-3">
          What We Do
        </span>
        <h2 className="text-xl sm:text-2xl font-bold text-[#333D51] tracking-tight">
          Services
        </h2>
      </div>

      {/* Toggle bar */}
      <div className={`flex ${isMob ? "flex-wrap" : ""} justify-center gap-2 mb-10`}>
        {services.map((s: any, i: number) => (
          <button
            key={s.id}
            onClick={() => switchTo(i)}
            className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-300
              ${
                activeIdx === i
                  ? "bg-[#0D2B7E] text-white shadow-md"
                  : "bg-white text-[#333D51] ring-1 ring-[#DFDFE0]/60 hover:ring-[#0D2B7E]/40 hover:text-[#0D2B7E]"
              }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Active service card — animated */}
      {active && (
        <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-[#DFDFE0]/60">
          {/* Subtle corner blob */}
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-bl from-[#EEF1FB]/70 to-transparent" />

          <div
            className={`relative grid ${isMob ? "grid-cols-1" : "grid-cols-2"} gap-6 p-6 sm:p-8 transition-all duration-300 ease-out ${contentClass}`}
          >
            {/* Image */}
            {active.content?.image && (
              <div className="overflow-hidden rounded-xl">
                <Image
                  src={active.content.image}
                  sizes="(max-width: 768px) 100vw, 560px"
                  alt={active.content?.heading || active.label}
                  className="w-full h-full object-cover rounded-xl transition-opacity duration-300"
                  style={{ maxHeight: 360 }}
                />
              </div>
            )}

            {/* Text */}
            <div className="flex flex-col justify-center">
              {active.content?.heading && (
                <h3 className="text-lg font-bold text-[#333D51] tracking-tight mb-1">
                  {active.content.heading}
                </h3>
              )}
              {active.content?.subheading && (
                <p className="text-sm font-medium text-[#0D2B7E] mb-3">
                  {active.content.subheading}
                </p>
              )}
              {active.content?.desc && (
                <p className="text-sm text-[#666D7C] leading-relaxed mb-5">
                  {active.content.desc}
                </p>
              )}

              {/* Highlights — staggered entry */}
              {active.content?.highlights?.length > 0 && (
                <ul className="space-y-2">
                  {active.content.highlights.map((h: string, idx: number) => (
                    <li
                      key={`${displayIdx}-${idx}`}
                      className="flex items-start gap-2 text-sm text-[#333D51] leading-relaxed animate-[fadeSlideUp_0.35s_ease-out_both]"
                      style={{ animationDelay: `${idx * 60}ms` }}
                    >
                      <svg
                        className="mt-0.5 h-4 w-4 shrink-0 text-[#28E454]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                      {h}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keyframe for staggered highlights */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};
